import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import Sidebar from "../components/Sidebar";

function slugify(text = "") {
  return text
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const CATEGORY_OPTIONS = [
  "Moteur",
  "Freinage",
  "Carrosserie",
  "Éclairage",
  "Suspension",
  "Électricité",
  "Filtration",
  "Pneumatiques",
  "Batterie",
  "Accessoires",
  "Climatisation",
  "Transmission",
  "Autre",
];

const BRAND_OPTIONS = [
  "Toyota",
  "Nissan",
  "Hyundai",
  "Kia",
  "Mercedes-Benz",
  "BMW",
  "Audi",
  "Ford",
  "Peugeot",
  "Renault",
  "Volkswagen",
  "Honda",
  "Suzuki",
  "Mitsubishi",
  "Isuzu",
  "Changan",
  "Jetour",
  "Autre",
];

const initialForm = {
  nom: "",
  slug: "",
  categorie: "",
  customCategorie: "",
  marque: "",
  customMarque: "",
  modeleCompatible: "",
  reference: "",
  prix: "",
  devise: "FCFA",
  stock: "",
  disponibilite: "en stock",
  etat: "neuf",
  description: "",
  imageFiles: [],
  imageUrls: [],
  contactWhatsApp: "+22879309400",
  isFeatured: false,
  isPromo: false,
  isPopular: false,
  livraisonRapide: false,
  origine: false,
  surCommande: false,
  enLigne: true,
};

export default function AdminPieces() {
  const [pieces, setPieces] = useState([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");

  const loadPieces = async () => {
    try {
      const snap = await getDocs(collection(db, "pieces_detachees"));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPieces(data);
    } catch (error) {
      console.error(error);
      setMessageType("danger");
      setMessage("Erreur lors du chargement des pièces.");
    }
  };

  useEffect(() => {
    loadPieces();
  }, []);

  const filteredPieces = useMemo(() => {
    const term = search.trim().toLowerCase();
    return pieces.filter((p) => {
      const text =
        `${p.nom || ""} ${p.categorie || ""} ${p.marque || ""} ${p.reference || ""}`.toLowerCase();
      return text.includes(term);
    });
  }, [pieces, search]);

  const openCreate = () => {
    setEditId(null);
    setForm(initialForm);
    setShowModal(true);
  };

  const openEdit = (piece) => {
    const categorieExists = CATEGORY_OPTIONS.includes(piece.categorie);
    const marqueExists = BRAND_OPTIONS.includes(piece.marque);

    setEditId(piece.id);
    setForm({
      nom: piece.nom || "",
      slug: piece.slug || "",
      categorie: categorieExists ? piece.categorie : "Autre",
      customCategorie: categorieExists ? "" : piece.categorie || "",
      marque: marqueExists ? piece.marque : "Autre",
      customMarque: marqueExists ? "" : piece.marque || "",
      modeleCompatible: Array.isArray(piece.modeleCompatible)
        ? piece.modeleCompatible.join(", ")
        : "",
      reference: piece.reference || "",
      prix: piece.prix || "",
      devise: piece.devise || "FCFA",
      stock: piece.stock || "",
      disponibilite: piece.disponibilite || "en stock",
      etat: piece.etat || "neuf",
      description: piece.description || "",
      imageFiles: [],
      imageUrls: Array.isArray(piece.imageUrls) ? piece.imageUrls : [],
      contactWhatsApp: piece.contactWhatsApp || "+22879309400",
      isFeatured: !!piece.isFeatured,
      isPromo: !!piece.isPromo,
      isPopular: !!piece.isPopular,
      livraisonRapide: !!piece.livraisonRapide,
      origine: !!piece.origine,
      surCommande: !!piece.surCommande,
      enLigne: piece.enLigne !== false,
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value;

    setForm((prev) => {
      const updated = { ...prev, [name]: nextValue };

      if (name === "nom" && !editId) {
        updated.slug = slugify(value);
      }

      if (name === "categorie" && value !== "Autre") {
        updated.customCategorie = "";
      }

      if (name === "marque" && value !== "Autre") {
        updated.customMarque = "";
      }

      return updated;
    });
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setForm((prev) => ({
      ...prev,
      imageFiles: files,
    }));
  };

  const removeExistingImage = (indexToRemove) => {
    setForm((prev) => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, index) => index !== indexToRemove),
    }));
  };

  const uploadImages = async () => {
    if (!form.imageFiles || form.imageFiles.length === 0) {
      return form.imageUrls || [];
    }

    const uploadedUrls = await Promise.all(
      form.imageFiles.map(async (file) => {
        const fileName = `${Date.now()}_${file.name}`;
        const storageRef = ref(storage, `pieces_detachees/${fileName}`);
        const snapshot = await uploadBytes(storageRef, file);
        return await getDownloadURL(snapshot.ref);
      })
    );

    return [...(form.imageUrls || []), ...uploadedUrls];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");

    try {
      const finalImageUrls = await uploadImages();

      const finalCategorie =
        form.categorie === "Autre"
          ? form.customCategorie.trim()
          : form.categorie.trim();

      const finalMarque =
        form.marque === "Autre"
          ? form.customMarque.trim()
          : form.marque.trim();

      const payload = {
        nom: form.nom.trim(),
        slug: form.slug.trim() || slugify(form.nom),
        categorie: finalCategorie,
        marque: finalMarque,
        modeleCompatible: form.modeleCompatible
          .split(",")
          .map((m) => m.trim())
          .filter(Boolean),
        reference: form.reference.trim(),
        prix: form.prix ? Number(form.prix) : 0,
        devise: form.devise,
        stock: form.stock ? Number(form.stock) : 0,
        disponibilite: form.disponibilite,
        etat: form.etat,
        description: form.description.trim(),
        imageUrls: finalImageUrls,
        contactWhatsApp: form.contactWhatsApp.trim(),
        isFeatured: form.isFeatured,
        isPromo: form.isPromo,
        isPopular: form.isPopular,
        livraisonRapide: form.livraisonRapide,
        origine: form.origine,
        surCommande: form.surCommande,
        enLigne: form.enLigne,
        updatedAt: serverTimestamp(),
      };

      if (!payload.categorie || !payload.marque) {
        throw new Error("La catégorie et la marque sont obligatoires.");
      }

      if (editId) {
        await updateDoc(doc(db, "pieces_detachees", editId), payload);
        setMessage("Pièce mise à jour avec succès.");
      } else {
        await addDoc(collection(db, "pieces_detachees"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        setMessage("Pièce ajoutée avec succès.");
      }

      setMessageType("success");
      setShowModal(false);
      setForm(initialForm);
      setEditId(null);
      await loadPieces();
    } catch (error) {
      console.error(error);
      setMessageType("danger");
      setMessage(
        error?.message || "Erreur lors de l’enregistrement."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Supprimer cette pièce ?");
    if (!ok) return;

    try {
      await deleteDoc(doc(db, "pieces_detachees", id));
      setPieces((prev) => prev.filter((p) => p.id !== id));
      setMessageType("success");
      setMessage("Pièce supprimée.");
    } catch (error) {
      console.error(error);
      setMessageType("danger");
      setMessage("Erreur lors de la suppression.");
    }
  };

  const toggleField = async (piece, field) => {
    try {
      await updateDoc(doc(db, "pieces_detachees", piece.id), {
        [field]: !piece[field],
        updatedAt: serverTimestamp(),
      });

      setPieces((prev) =>
        prev.map((p) =>
          p.id === piece.id ? { ...p, [field]: !p[field] } : p
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="d-flex flex-column flex-md-row">
      <Sidebar />

      <main className="flex-grow-1 p-4" style={{ marginLeft: "250px" }}>
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <div>
              <h2 className="fw-bold mb-1">Gestion des pièces détachées</h2>
              <p className="text-muted mb-0">
                Ajoutez, modifiez et mettez en avant les pièces disponibles.
              </p>
            </div>

            <button className="btn btn-primary" onClick={openCreate}>
              + Ajouter une pièce
            </button>
          </div>

          {message && (
            <div className={`alert alert-${messageType}`}>{message}</div>
          )}

          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <input
                type="text"
                className="form-control"
                placeholder="Rechercher une pièce, une catégorie, une marque, une référence..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="row g-4">
            {filteredPieces.map((piece) => (
              <div key={piece.id} className="col-md-6 col-xl-4">
                <div className="card h-100 shadow-sm border-0">
                  <img
                    src={piece.imageUrls?.[0] || "/assets/default.jpg"}
                    alt={piece.nom}
                    className="card-img-top"
                    style={{ height: "220px", objectFit: "cover" }}
                  />

                  <div className="card-body d-flex flex-column">
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {piece.isFeatured && (
                        <span className="badge bg-warning text-dark">Vedette</span>
                      )}
                      {piece.isPromo && (
                        <span className="badge bg-danger">Promo</span>
                      )}
                      {piece.isPopular && (
                        <span className="badge bg-info text-dark">Populaire</span>
                      )}
                      {piece.livraisonRapide && (
                        <span className="badge bg-success">Livraison rapide</span>
                      )}
                      {piece.origine && (
                        <span className="badge bg-dark">Origine</span>
                      )}
                      {piece.surCommande && (
                        <span className="badge bg-primary">Sur commande</span>
                      )}
                      {piece.enLigne ? (
                        <span className="badge bg-success">En ligne</span>
                      ) : (
                        <span className="badge bg-secondary">Hors ligne</span>
                      )}
                    </div>

                    <h5 className="fw-bold">{piece.nom}</h5>
                    <p className="text-muted small mb-1">
                      {piece.categorie} • {piece.marque}
                    </p>
                    <p className="text-muted small mb-2">
                      Réf : {piece.reference || "—"}
                    </p>
                    <p className="fw-bold text-primary mb-2">
                      {piece.prix > 0
                        ? `${Number(piece.prix).toLocaleString("fr-FR")} FCFA`
                        : "Prix sur demande"}
                    </p>
                    <p className="small mb-3">
                      Stock : {piece.stock || 0} • {piece.disponibilite}
                    </p>

                    <div className="mt-auto d-flex flex-wrap gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openEdit(piece)}
                      >
                        Modifier
                      </button>

                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(piece.id)}
                      >
                        Supprimer
                      </button>

                      <button
                        className="btn btn-sm btn-outline-warning"
                        onClick={() => toggleField(piece, "isFeatured")}
                      >
                        Vedette
                      </button>

                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() => toggleField(piece, "enLigne")}
                      >
                        {piece.enLigne ? "Masquer" : "Publier"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filteredPieces.length === 0 && (
              <div className="col-12">
                <div className="alert alert-light border text-center">
                  Aucune pièce trouvée.
                </div>
              </div>
            )}
          </div>

          {showModal && (
            <div
              className="modal show fade d-block"
              tabIndex="-1"
              style={{
                backgroundColor: "rgba(0,0,0,0.55)",
                overflowY: "auto",
              }}
            >
              <div
                className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
                style={{
                  minHeight: "100vh",
                  paddingTop: "20px",
                  paddingBottom: "20px",
                }}
              >
                <div className="modal-content border-0 shadow-lg">
                  <form onSubmit={handleSubmit}>
                    <div
                      className="modal-header sticky-top bg-white"
                      style={{ zIndex: 10 }}
                    >
                      <h5 className="modal-title">
                        {editId ? "Modifier la pièce" : "Ajouter une pièce"}
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowModal(false)}
                      ></button>
                    </div>

                    <div
                      className="modal-body px-4 py-3"
                      style={{
                        maxHeight: "70vh",
                        overflowY: "auto",
                      }}
                    >
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">Nom</label>
                          <input
                            className="form-control"
                            name="nom"
                            value={form.nom}
                            onChange={handleChange}
                            required
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Slug</label>
                          <input
                            className="form-control"
                            name="slug"
                            value={form.slug}
                            onChange={handleChange}
                            required
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Catégorie</label>
                          <select
                            className="form-select"
                            name="categorie"
                            value={form.categorie}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Choisir une catégorie</option>
                            {CATEGORY_OPTIONS.map((category) => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Marque</label>
                          <select
                            className="form-select"
                            name="marque"
                            value={form.marque}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Choisir une marque</option>
                            {BRAND_OPTIONS.map((brand) => (
                              <option key={brand} value={brand}>
                                {brand}
                              </option>
                            ))}
                          </select>
                        </div>

                        {form.categorie === "Autre" && (
                          <div className="col-md-6">
                            <label className="form-label">Nouvelle catégorie</label>
                            <input
                              className="form-control"
                              name="customCategorie"
                              value={form.customCategorie}
                              onChange={handleChange}
                              placeholder="Saisir la catégorie"
                              required
                            />
                          </div>
                        )}

                        {form.marque === "Autre" && (
                          <div className="col-md-6">
                            <label className="form-label">Nouvelle marque</label>
                            <input
                              className="form-control"
                              name="customMarque"
                              value={form.customMarque}
                              onChange={handleChange}
                              placeholder="Saisir la marque"
                              required
                            />
                          </div>
                        )}

                        <div className="col-md-6">
                          <label className="form-label">
                            Modèles compatibles
                          </label>
                          <input
                            className="form-control"
                            name="modeleCompatible"
                            value={form.modeleCompatible}
                            onChange={handleChange}
                            placeholder="Hilux, Fortuner, Corolla"
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Référence</label>
                          <input
                            className="form-control"
                            name="reference"
                            value={form.reference}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="form-label">Prix</label>
                          <input
                            type="number"
                            className="form-control"
                            name="prix"
                            value={form.prix}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="form-label">Devise</label>
                          <select
                            className="form-select"
                            name="devise"
                            value={form.devise}
                            onChange={handleChange}
                          >
                            <option value="FCFA">FCFA</option>
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                          </select>
                        </div>

                        <div className="col-md-3">
                          <label className="form-label">Stock</label>
                          <input
                            type="number"
                            className="form-control"
                            name="stock"
                            value={form.stock}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="form-label">État</label>
                          <select
                            className="form-select"
                            name="etat"
                            value={form.etat}
                            onChange={handleChange}
                          >
                            <option value="neuf">Neuf</option>
                            <option value="occasion">Occasion</option>
                          </select>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Disponibilité</label>
                          <select
                            className="form-select"
                            name="disponibilite"
                            value={form.disponibilite}
                            onChange={handleChange}
                          >
                            <option value="en stock">En stock</option>
                            <option value="sur commande">Sur commande</option>
                            <option value="rupture">Rupture</option>
                          </select>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">WhatsApp</label>
                          <input
                            className="form-control"
                            name="contactWhatsApp"
                            value={form.contactWhatsApp}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-12">
                          <label className="form-label">Description</label>
                          <textarea
                            className="form-control"
                            rows="4"
                            name="description"
                            value={form.description}
                            onChange={handleChange}
                          ></textarea>
                        </div>

                        <div className="col-12">
                          <label className="form-label">Upload des images</label>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="form-control"
                            onChange={handleImagesChange}
                          />
                          <div className="form-text">
                            Vous pouvez sélectionner une ou plusieurs images.
                          </div>
                        </div>

                        {(form.imageUrls.length > 0 || form.imageFiles.length > 0) && (
                          <div className="col-12">
                            <label className="form-label">Aperçu</label>
                            <div className="d-flex flex-wrap gap-3">
                              {form.imageUrls.map((url, index) => (
                                <div
                                  key={`existing-${index}`}
                                  className="position-relative"
                                >
                                  <img
                                    src={url}
                                    alt={`Pièce ${index + 1}`}
                                    className="rounded border"
                                    style={{
                                      width: "90px",
                                      height: "90px",
                                      objectFit: "cover",
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger position-absolute top-0 end-0"
                                    onClick={() => removeExistingImage(index)}
                                    style={{ transform: "translate(30%, -30%)" }}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}

                              {form.imageFiles.map((file, index) => (
                                <div key={`new-${index}`}>
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={`Nouveau fichier ${index + 1}`}
                                    className="rounded border"
                                    style={{
                                      width: "90px",
                                      height: "90px",
                                      objectFit: "cover",
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="col-12">
                          <label className="form-label fw-semibold">
                            Mise en avant & badges
                          </label>

                          <div className="row g-2">
                            <div className="col-md-3">
                              <div className="form-check border rounded p-2 h-100">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  name="isFeatured"
                                  checked={form.isFeatured}
                                  onChange={handleChange}
                                  id="isFeatured"
                                />
                                <label className="form-check-label" htmlFor="isFeatured">
                                  Mettre en vedette
                                </label>
                              </div>
                            </div>

                            <div className="col-md-3">
                              <div className="form-check border rounded p-2 h-100">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  name="isPromo"
                                  checked={form.isPromo}
                                  onChange={handleChange}
                                  id="isPromo"
                                />
                                <label className="form-check-label" htmlFor="isPromo">
                                  Promo
                                </label>
                              </div>
                            </div>

                            <div className="col-md-3">
                              <div className="form-check border rounded p-2 h-100">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  name="isPopular"
                                  checked={form.isPopular}
                                  onChange={handleChange}
                                  id="isPopular"
                                />
                                <label className="form-check-label" htmlFor="isPopular">
                                  Populaire
                                </label>
                              </div>
                            </div>

                            <div className="col-md-3">
                              <div className="form-check border rounded p-2 h-100">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  name="enLigne"
                                  checked={form.enLigne}
                                  onChange={handleChange}
                                  id="enLigne"
                                />
                                <label className="form-check-label" htmlFor="enLigne">
                                  Publier sur le site
                                </label>
                              </div>
                            </div>

                            <div className="col-md-4">
                              <div className="form-check border rounded p-2 h-100">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  name="livraisonRapide"
                                  checked={form.livraisonRapide}
                                  onChange={handleChange}
                                  id="livraisonRapide"
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="livraisonRapide"
                                >
                                  Livraison rapide
                                </label>
                              </div>
                            </div>

                            <div className="col-md-4">
                              <div className="form-check border rounded p-2 h-100">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  name="origine"
                                  checked={form.origine}
                                  onChange={handleChange}
                                  id="origine"
                                />
                                <label className="form-check-label" htmlFor="origine">
                                  Pièce d'origine
                                </label>
                              </div>
                            </div>

                            <div className="col-md-4">
                              <div className="form-check border rounded p-2 h-100">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  name="surCommande"
                                  checked={form.surCommande}
                                  onChange={handleChange}
                                  id="surCommande"
                                />
                                <label className="form-check-label" htmlFor="surCommande">
                                  Sur commande
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      className="modal-footer sticky-bottom bg-white"
                      style={{ zIndex: 10 }}
                    >
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowModal(false)}
                      >
                        Annuler
                      </button>
                      <button className="btn btn-primary" disabled={saving}>
                        {saving ? "Enregistrement..." : "Enregistrer"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          <style>{`
            @media (max-width: 991.98px) {
              main[style] {
                margin-left: 0 !important;
              }
            }

            .modal-body::-webkit-scrollbar {
              width: 8px;
            }

            .modal-body::-webkit-scrollbar-thumb {
              background: rgba(0,0,0,0.2);
              border-radius: 20px;
            }
          `}</style>
        </div>
      </main>
    </div>
  );
}
